import { useEffect, useState } from 'react'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import { Button } from '@agms/ui'
import { apiErrorMessage } from '../../hooks/useErrorHandler'
import { helpApi, type ArticleStatus } from '../../lib/api'
import { CardTitle, SurfaceCard } from '../ui'

function toLocalInput(iso: string | null | undefined): string {
  if (!iso) return ''
  const date = new Date(iso)
  if (Number.isNaN(date.getTime())) return ''
  const pad = (value: number) => String(value).padStart(2, '0')
  return `${date.getFullYear()}-${pad(date.getMonth() + 1)}-${pad(date.getDate())}T${pad(date.getHours())}:${pad(date.getMinutes())}`
}

function statusText(active: boolean, startsAt: string, endsAt: string | null, status: ArticleStatus): string {
  if (status === 'archived') {
    return 'Hidden because this article is archived. Archived articles are not published again. Clear removes the stored window.'
  }
  if (status !== 'published') return 'Hidden because this article is not published.'
  if (active) return 'Showing now.'
  if (new Date(startsAt).getTime() > Date.now()) return 'Starts later. It is hidden until the start time.'
  if (endsAt) return 'Ended. It is hidden.'
  return 'Hidden.'
}

export function KnownIssuePinForm({ slug, status }: { slug: string; status: ArticleStatus }) {
  const queryClient = useQueryClient()
  const [start, setStart] = useState('')
  const [end, setEnd] = useState('')
  const [error, setError] = useState<string | null>(null)
  const published = status === 'published'

  const knownIssue = useQuery({
    queryKey: ['help-known-issue', slug],
    queryFn: () => helpApi.articleKnownIssue(slug),
    enabled: slug.length > 0,
    retry: false,
  })

  useEffect(() => {
    const issue = knownIssue.data?.known_issue
    setStart(toLocalInput(issue?.starts_at))
    setEnd(toLocalInput(issue?.ends_at))
  }, [knownIssue.data])

  const refresh = () => {
    void queryClient.invalidateQueries({ queryKey: ['help-known-issue', slug] })
    void queryClient.invalidateQueries({ queryKey: ['help-known-issues'] })
  }

  const save = useMutation({
    mutationFn: () => helpApi.saveKnownIssue(slug, {
      starts_at: new Date(start).toISOString(),
      ends_at: end.trim() === '' ? null : new Date(end).toISOString(),
    }),
    onSuccess: (response) => {
      setError(null)
      queryClient.setQueryData(['help-known-issue', slug], response)
      void queryClient.invalidateQueries({ queryKey: ['help-known-issues'] })
    },
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not save this known issue.'))
    },
  })

  const clear = useMutation({
    mutationFn: () => helpApi.clearKnownIssue(slug),
    onSuccess: () => {
      setError(null)
      setStart('')
      setEnd('')
      queryClient.setQueryData(['help-known-issue', slug], { known_issue: null })
      refresh()
    },
    onError: (mutationError) => {
      setError(apiErrorMessage(mutationError, 'Could not clear this known issue.'))
    },
  })

  const issue = knownIssue.data?.known_issue ?? null
  if (!published && !issue) return null

  return (
    <SurfaceCard>
      <CardTitle>Known issue</CardTitle>
      <p className="mt-2 text-sm text-slate-600">
        Saving a known issue does not store ticket text. On the new-ticket form, staff who can view tickets can link the new ticket to this article. That link is related. It does not mark this article as the one that solved the ticket.
      </p>
      {issue ? (
        <p className="mt-2 text-sm font-medium text-slate-800">{statusText(issue.active, issue.starts_at, issue.ends_at, status)}</p>
      ) : null}
      {error ? (
        <p className="mt-3 text-sm text-rose-700" role="alert">{error}</p>
      ) : null}
      {published ? (
        <div className="mt-4 grid gap-3 md:grid-cols-2">
          <p className="text-sm text-slate-600 md:col-span-2">
            {`Start and end use your time zone (${Intl.DateTimeFormat().resolvedOptions().timeZone}). Saved times are UTC.`}
          </p>
          <label className="text-sm font-medium text-slate-700">
            Known issue start
            <input
              type="datetime-local"
              value={start}
              onChange={(event) => setStart(event.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
          <label className="text-sm font-medium text-slate-700">
            Known issue end
            <input
              type="datetime-local"
              value={end}
              onChange={(event) => setEnd(event.target.value)}
              className="mt-1 block w-full rounded-md border border-slate-300 px-3 py-2 text-sm"
            />
          </label>
        </div>
      ) : null}
      <div className="mt-4 flex flex-wrap gap-2">
        {published ? (
          <Button onClick={() => save.mutate()} disabled={start.trim() === '' || save.isPending}>
            Save known issue
          </Button>
        ) : null}
        {issue ? (
          <Button variant="secondary" onClick={() => clear.mutate()} disabled={clear.isPending}>
            Clear known issue
          </Button>
        ) : null}
      </div>
    </SurfaceCard>
  )
}
